/**
 * Editable grid of classrooms (name, floor, capacity) for establishment forms.
 */

import * as React from 'react';
import { DoorOpen, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { cn } from '@/lib/utils';
import type { Classroom } from '@/types/establishment.types';

/** Props for {@link ClassroomsGrid}. */
interface ClassroomsGridProps {
  value?: Classroom[];
  onChange?: (classrooms: Classroom[]) => void;
  disabled?: boolean;
  className?: string;
}

/** Editable grid of establishment classrooms. */
export const ClassroomsGrid: React.FC<ClassroomsGridProps> = ({
  value = [],
  onChange,
  disabled = false,
  className,
}) => {
  const classrooms = value || [];

  /** Append an empty classroom (capacity left unset until the user fills it). */
  const addClassroom = () => {
    const newClassroom = {
      name: '',
      label: '',
      capacity: undefined,
    } as Classroom;
    onChange?.([...classrooms, newClassroom]);
  };

  /** Drop the classroom at `index` and emit the new list. */
  const removeClassroom = (index: number) => {
    const next = classrooms.filter((_, i) => i !== index);
    onChange?.(next);
  };

  /** Patch one field on the classroom at `index`. */
  const updateClassroom = (
    index: number,
    field: keyof Classroom,
    fieldValue: string | number | undefined,
  ) => {
    const next = [...classrooms];
    next[index] = { ...next[index], [field]: fieldValue };
    onChange?.(next);
  };

  return (
    <div className={cn('space-y-4', className)}>
      {classrooms.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 px-4 rounded-lg border-2 border-dashed border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] bg-[color-mix(in_srgb,var(--color-1-nuance-3)_5%,transparent)]">
          <DoorOpen className="w-8 h-8 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground mb-3">Aucune salle définie</p>
          <Button
            variant="outline"
            size="sm"
            icon={Plus}
            iconPosition="left"
            onClick={addClassroom}
            disabled={disabled}
          >
            Ajouter une salle
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {classrooms.map((classroom, index) => (
              <div
                key={classroom.id || `new-classroom-${index}`}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border transition-all',
                  'border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)]',
                  'bg-[color-mix(in_srgb,var(--color-1-nuance-3)_3%,transparent)]',
                  'hover:bg-[color-mix(in_srgb,var(--color-1-nuance-3)_5%,transparent)]',
                  'hover:border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)]',
                  'animate-fade-in',
                )}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)]">
                  <DoorOpen className="w-4 h-4 text-[var(--color-2-nuance-1)]" />
                </div>

                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="relative">
                    <label
                      htmlFor={`classroom-name-${index}`}
                      className="text-xs font-medium text-[var(--color-1-nuance-1)] mb-1 block"
                    >
                      Nom de la salle <span className="text-error">*</span>
                    </label>
                    <input
                      id={`classroom-name-${index}`}
                      type="text"
                      value={classroom.name}
                      onChange={(e) => updateClassroom(index, 'name', e.target.value)}
                      disabled={disabled}
                      placeholder="Ex: Salle 101"
                      maxLength={100}
                      className={cn(
                        'w-full px-3 py-2 rounded-lg text-sm',
                        'bg-background border border-[var(--color-1-nuance-1)]',
                        'text-[var(--color-2-nuance-1)]',
                        'dark:border-[var(--color-2-nuance-1)] dark:text-[var(--color-1-nuance-1)]',
                        'focus:outline-none focus:ring-2 focus:ring-[var(--color-2-nuance-1)] focus:border-transparent',
                        'transition-all duration-200',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        !classroom.name && 'border-error',
                      )}
                    />
                  </div>

                  <div className="relative">
                    <label
                      htmlFor={`classroom-label-${index}`}
                      className="text-xs font-medium text-[var(--color-1-nuance-1)] mb-1 block"
                    >
                      Label
                    </label>
                    <input
                      id={`classroom-label-${index}`}
                      type="text"
                      value={classroom.label || ''}
                      onChange={(e) => updateClassroom(index, 'label', e.target.value)}
                      disabled={disabled}
                      placeholder="Ex: Premier étage"
                      maxLength={255}
                      className={cn(
                        'w-full px-3 py-2 rounded-lg text-sm',
                        'bg-background border border-[var(--color-1-nuance-1)]',
                        'text-[var(--color-2-nuance-1)]',
                        'dark:border-[var(--color-2-nuance-1)] dark:text-[var(--color-1-nuance-1)]',
                        'focus:outline-none focus:ring-2 focus:ring-[var(--color-2-nuance-1)] focus:border-transparent',
                        'transition-all duration-200',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                      )}
                    />
                  </div>

                  <div className="relative">
                    <label
                      htmlFor={`classroom-capacity-${index}`}
                      className="text-xs font-medium text-[var(--color-1-nuance-1)] mb-1 block"
                    >
                      Capacité
                    </label>
                    <input
                      id={`classroom-capacity-${index}`}
                      type="number"
                      value={classroom.capacity ?? ''}
                      onChange={(e) =>
                        updateClassroom(
                          index,
                          'capacity',
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
                      disabled={disabled}
                      placeholder="Ex: 30"
                      min={0}
                      className={cn(
                        'w-full px-3 py-2 rounded-lg text-sm',
                        'bg-background border border-[var(--color-1-nuance-1)]',
                        'text-[var(--color-2-nuance-1)]',
                        'dark:border-[var(--color-2-nuance-1)] dark:text-[var(--color-1-nuance-1)]',
                        'focus:outline-none focus:ring-2 focus:ring-[var(--color-2-nuance-1)] focus:border-transparent',
                        'transition-all duration-200',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                      )}
                    />
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeClassroom(index)}
                  disabled={disabled}
                  icon={Trash2}
                  className="text-error hover:text-error hover:bg-error-light"
                />
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            icon={Plus}
            iconPosition="left"
            onClick={addClassroom}
            disabled={disabled}
            className="w-full"
          >
            Ajouter une salle
          </Button>
        </>
      )}
    </div>
  );
};
